<template>
  <div id="printable">
    <div v-if="isDataLoading">
      {{ $t('pages.loading') }}
    </div>
    <group-table-view
      v-else
      :table-data="tableData"
      :empty-result-string="$t('pages.noData')"
      :columns="columns"
      @changeSort="changeSort"
      @filterClick="filterClick"
      @update="fetch"
      @row-click="onRowClick"
    />
    <additional v-if="rowActiveId" :row="activeRow" :link="computerActiveId" @image-dblclick="openModal" />
    <modal v-if="isShowModal" :title="activeRow.caption" :fullscreen="true" @close="close">
      <div :class="$style.content">
        <div :class="$style.imageWrapper">
          <img :src="activeLink">
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Vue from 'vue';
import VueToast from 'vue-toast-notification';
import { getMessengersAttachment, getMessengersAttachmentLink } from '@/api/methods/computers';
import { formatDate, getTime } from '@/helpers/format-date';
import { getMessengerIconById } from '@/helpers/messengers';
import workerBasedRequestMixin from '@/components/common/logs/mixins/workerBasedRequestMixin';
import IconMessageDirectionInbox from '@/assets/images/icons/message-direction/inbox.svg';
import IconMessageDirectionOutbox from '@/assets/images/icons/message-direction/outbox.svg';
import GroupTableView from '@/components/common/table/tableWithGroupingView.vue';
import { dateTime } from '@/helpers/filters';
import { LogData } from '@/domains/log/data';
import Modal from '@/components/common/modal/index.vue';
import Additional from './components-default/additional.vue';
import { messengerName, directionName } from './filters';

Vue.use(VueToast);

export default {
  components: {
    GroupTableView,
    Additional,
    Modal,
  },
  mixins: [workerBasedRequestMixin],
  data() {
    return {
      tableData: [],
      columns: [
        {
          name: this.$t('pages.columns.time'),
          width: '5%',
          widthNum: 15,
          field: 'date',
          sort: 1,
          index: 0,
          filter: [],
          selected: [],
          showFilter: false,
        },
        {
          name: this.$t('pages.columns.messenger'),
          width: '5%',
          widthNum: 15,
          field: 'messenger',
          sort: 0,
          index: 1,
          filter: [],
          selected: [],
          showFilter: false,
        },
        {
          name: this.$t('pages.columns.buddy'),
          width: '5%',
          widthNum: 16,
          field: 'interlocutor',
          sort: 0,
          index: 2,
          filter: [],
          selected: [],
          showFilter: false,
        },
        {
          name: this.$t('pages.columns.messageType'),
          width: '5%',
          widthNum: 16,
          field: 'direction',
          sort: 0,
          index: 3,
          filter: [],
          selected: [],
          showFilter: false,
        },
        {
          name: this.$t('pages.columns.messageTextOrFile'),
          width: '8%',
          widthNum: 25,
          field: 'text',
          sort: 0,
          index: 4,
          filter: [],
          selected: [],
          showFilter: false,
        },
        {
          name: this.$t('pages.columns.username'),
          width: '5%',
          widthNum: 15,
          field: 'username',
          sort: 0,
          index: 5,
          filter: [],
          selected: [],
          showFilter: false,
        },
      ],
    };
  },
  computed: {
    isShowModal() {
      return Object.prototype.hasOwnProperty.call(this.$route.query, 'modal');
    },
    rowActiveId() {
      return this.$route.params.record;
    },
    computerActiveId() {
      return this.$route.params.computer;
    },
    activeRow() {
      const activeRow = this.pagedData.find((i) => i.id === this.rowActiveId);
      return activeRow || {};
    },
    activeLink() {
      return getMessengersAttachmentLink(parseInt(this.$route.params.computer, 10), this.rowActiveId);
    },
  },
  watch: {
    '$route.query': function (query, oldQuery) {
      // eslint-disable-line func-names
      if (query.dateFrom !== oldQuery.dateFrom || query.dateTo !== oldQuery.dateTo) {
        this.fetch();
      }
      if (query.filter !== oldQuery.filter) {
        this.getTableData();
      }
    },
    pagedData() {
      this.getTableData();
    },
  },
  async created() {
    this.$store.commit('logs/printData/setColumns', this.columns);
    await this.fetch();
  },
  methods: {
    assembleColumnFilter() {
      this.columns.forEach((column) => {
        const filter = [];

        this.tableData.forEach((item) => {
          if (item[column.field] !== undefined && filter.indexOf(item[column.field]) === -1) {
            filter.push(item[column.field]);
          }
        });
        column.filter = filter;
      });
    },
    changeSort(column) {
      const { sort } = column;
      this.columns.forEach((col) => {
        col.sort = 0;
      });

      if (sort === 1) {
        column.sort = 2;
      } else column.sort = 1;
    },
    filterClick(column) {
      const value = column.showFilter;
      column.showFilter = !value;
      if (column.showFilter) {
        window.addEventListener('click', (e) => {
          if (
            !document.getElementById(`columnFilter${column.field}`).contains(e.target)
              && !document.getElementById(`filterIcon${column.field}`).contains(e.target)
          ) {
            column.showFilter = false;
            window.removeEventListener('click', e);
          }
        });
      }
    },
    getTableData() {
      let items = [...this.pagedData];
      items = items.filter((element) => element !== undefined);
      if (items.length > 0) {
        this.tableData = LogData.filterTable(
          items,
          [
            (item) => dateTime(item.date),
            (item) => directionName(item.direction),
            'interlocutor',
            (item) => messengerName(item.messenger),
            'text',
            'username',
          ],
          this.$route.query.filter,
        );

        this.tableData = this.tableData.map((item) => ({
          ...item,
          date: `${formatDate(item.date)} ${getTime(item.date)}`,
          direction: directionName(item.direction),
          directionIcon: this.getMessageDirectionIcon(item.direction),
          messenger: messengerName(item.messenger),
          messengerIcon: getMessengerIconById(item.messenger),
        }));
      }
      this.assembleColumnFilter();
    },
    openModal() {
      this.$router.replace({
        query: {
          ...this.$route.query,
          modal: null,
        },
      });
    },
    close() {
      const query = { ...this.$route.query };
      delete query.modal;

      this.$router.replace({
        query,
      });
    },
    onRowClick(id) {
      this.$router.push({
        name: 'computers-computer-log-record',
        params: { record: id },
        query: this.$route.query,
      });
    },
    getMessageDirectionIcon(direction) {
      switch (direction) {
        case 31:
          return IconMessageDirectionInbox;
        case 32:
          return IconMessageDirectionOutbox;
        default:
          return null;
      }
    },
    async generateAttachmentLink(recordId) {
      const result = await getMessengersAttachment(parseInt(this.$route.params.computer, 10), recordId);
      if (!result.success) {
        Vue.$toast.open({
          message: result.message,
          type: 'error',
        });
      }
    },
  },
};
</script>

<style lang="scss" module>
  .content {
    height: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .imageWrapper {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    padding: 30px;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      object-position: center;
    }
  }
</style>
